import { graphqlClient } from 'lib/graphql-client';
import {
  IGetPlatformDictionaryPayload,
  IGetPlatformDictionaryResponse,
  IPlatformDictionaryMutationResponse,
  PlatformDictionaryInput,
  PlatformDictionaryItem,
} from 'types/platform-dictionary.types';

const ITEM_FIELDS = `
  ItemId
  Key
  Value
  Tags
  Language
  OrganizationId
  CreatedDate
  LastUpdatedDate
  CreatedBy
  LastUpdatedBy
`;

const MUTATION_FIELDS = `
  acknowledged
  itemId
  totalImpactedData
  message
`;

/**
 * Writes a JS value as a GraphQL input literal (unquoted object keys).
 * Values are inlined rather than sent as variables because the gateway's
 * input type names are not known here. JSON.stringify escapes strings safely.
 */
export const toGraphqlLiteral = (value: unknown): string => {
  if (Array.isArray(value)) {
    return `[${value.map(toGraphqlLiteral).join(', ')}]`;
  }
  if (value !== null && typeof value === 'object') {
    const fields = Object.entries(value)
      .filter(([, fieldValue]) => fieldValue !== undefined)
      .map(([key, fieldValue]) => `${key}: ${toGraphqlLiteral(fieldValue)}`);
    return `{ ${fields.join(', ')} }`;
  }
  return JSON.stringify(value);
};

// Update/delete always target one ItemId so an empty `where: {}` can never hit every row.
const whereItemId = (itemId: string) => {
  if (!itemId) throw new Error('itemId is required');
  return toGraphqlLiteral({ ItemId: { eq: itemId } });
};

export const getPlatformDictionaries = async ({
  where = {},
  pageNo = 1,
  pageSize = 10,
}: IGetPlatformDictionaryPayload = {}) => {
  const query = `
    query {
      getPlatformDictionarys(
        where: ${toGraphqlLiteral(where)}
        order: []
        paging: ${toGraphqlLiteral({ pageNo, pageSize })}
      ) {
        items { ${ITEM_FIELDS} }
        totalCount
        pageNo
        pageSize
        totalPages
        hasNextPage
        hasPreviousPage
      }
    }
  `;
  const data = await graphqlClient.query<{
    getPlatformDictionarys: IGetPlatformDictionaryResponse;
  }>({ query });
  return data.getPlatformDictionarys;
};

export const getPlatformDictionaryByKey = async (
  key: string
): Promise<PlatformDictionaryItem | null> => {
  const response = await getPlatformDictionaries({
    where: { Key: { eq: key } },
    pageSize: 1,
  });
  // Checked here too: if the gateway ignored the filter, a save would overwrite another row.
  return response?.items?.find((item) => item.Key === key) ?? null;
};

export const createPlatformDictionary = async (input: PlatformDictionaryInput) => {
  const query = `
    mutation {
      insertPlatformDictionary(input: ${toGraphqlLiteral(input)}) { ${MUTATION_FIELDS} }
    }
  `;
  const data = await graphqlClient.mutate<{
    insertPlatformDictionary: IPlatformDictionaryMutationResponse;
  }>({ query });
  return data.insertPlatformDictionary;
};

export const createManyPlatformDictionaries = async (inputs: PlatformDictionaryInput[]) => {
  const query = `
    mutation {
      insertManyPlatformDictionary(input: ${toGraphqlLiteral(inputs)}) {
        acknowledged
        totalImpactedData
        message
        itemIds
      }
    }
  `;
  const data = await graphqlClient.mutate<{
    insertManyPlatformDictionary: IPlatformDictionaryMutationResponse;
  }>({ query });
  return data.insertManyPlatformDictionary;
};

export const updatePlatformDictionary = async (
  itemId: string,
  input: PlatformDictionaryInput
) => {
  const query = `
    mutation {
      updatePlatformDictionary(
        where: ${whereItemId(itemId)}
        input: ${toGraphqlLiteral(input)}
      ) { ${MUTATION_FIELDS} }
    }
  `;
  const data = await graphqlClient.mutate<{
    updatePlatformDictionary: IPlatformDictionaryMutationResponse;
  }>({ query });
  return data.updatePlatformDictionary;
};

// Key is unique in the schema, so update the existing row instead of inserting a second one.
export const savePlatformDictionaryValue = async (input: PlatformDictionaryInput) => {
  const existing = await getPlatformDictionaryByKey(input.Key);
  if (existing) return updatePlatformDictionary(existing.ItemId, input);
  return createPlatformDictionary(input);
};

// The gateway has no single `deletePlatformDictionary`; deleteMany filtered by one ItemId removes one row.
export const deletePlatformDictionary = async (itemId: string, isHardDelete = false) => {
  const query = `
    mutation {
      deleteManyPlatformDictionary(
        where: ${whereItemId(itemId)}
        input: ${toGraphqlLiteral({ isHardDelete })}
      ) { ${MUTATION_FIELDS} }
    }
  `;
  const data = await graphqlClient.mutate<{
    deleteManyPlatformDictionary: IPlatformDictionaryMutationResponse;
  }>({ query });
  return data.deleteManyPlatformDictionary;
};
